import { format } from "date-fns";
import { fr } from "date-fns/locale";
import { CalendarIcon } from "lucide-react";
import { useState } from "react";

import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  capToNow,
  toTimeInputValue,
  withCurrentTimeOfDay,
  withDayOf,
  withTimeInputValue,
} from "@/lib/event-date";

interface EventDateTimePickerProps {
  id?: string;
  /** `undefined` means "maintenant" — nothing is sent and the API stamps `now()`. */
  value: Date | undefined;
  onChange: (value: Date | undefined) => void;
}

/**
 * Date + time picker for a claim timeline event, shared by `SendEventModal` and
 * `ClaimsBatchIsland` so the two cannot drift apart again — they previously held one copy of
 * the picker each, and therefore one copy each of the TECH-892 stale-date bug.
 *
 * Laid out like the generic `ui/datetime-picker`, but deliberately not that component: this one
 * carries event-specific rules the generic picker gets wrong for a timeline entry.
 *
 *  - **`undefined` means "maintenant".** The trigger reads "Aujourd'hui" and nothing is sent, so
 *     the API stamps `now()` at insert time. It must not silently become a concrete timestamp
 *     just because the popover was opened — that was TECH-892.
 *  - **A picked day never keeps midnight.** `react-day-picker` returns local midnight; the
 *     generic picker defaults the time to `00:00`, which would backdate the event behind
 *     everything that already happened that day.
 *  - **Future datetimes are capped to now.** Future days are disabled, but once minutes exist an
 *     admin can pick today and type 23:00 at 10:00.
 *
 * Clearing the time field is ignored rather than treated as a reset: native time inputs report
 * an empty value mid-typing, so honouring it would wipe the chosen day as the admin types. The
 * "Maintenant" button in the footer is the way back to `undefined`.
 */
export function EventDateTimePicker({ id, value, onChange }: EventDateTimePickerProps) {
  const [open, setOpen] = useState(false);

  function selectDay(day: Date | undefined) {
    if (!day) {
      return;
    }
    // A time the admin typed deliberately outlives a change of day; otherwise take the clock.
    onChange(capToNow(value ? withDayOf(value, day) : withCurrentTimeOfDay(day)));
    setOpen(false);
  }

  function selectTime(timeValue: string) {
    const next = withTimeInputValue(value ?? new Date(), timeValue);
    if (!next) {
      return;
    }
    onChange(capToNow(next));
  }

  return (
    <div className="flex gap-2">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger
          render={<Button id={id} variant="outline" className="flex-1 justify-start font-normal" />}
        >
          <CalendarIcon data-icon="inline-start" />
          {value ? format(value, "d MMMM yyyy", { locale: fr }) : "Aujourd'hui"}
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="start">
          <Calendar
            mode="single"
            locale={fr}
            selected={value}
            onSelect={selectDay}
            disabled={{ after: new Date() }}
          />
          <div className="border-t p-2">
            <Button
              type="button"
              variant="ghost"
              className="w-full font-normal"
              onClick={() => {
                onChange(undefined);
                setOpen(false);
              }}
            >
              Maintenant
            </Button>
          </div>
        </PopoverContent>
      </Popover>
      <Input
        type="time"
        aria-label="Heure de l'évènement"
        className="w-28"
        value={toTimeInputValue(value)}
        onChange={(event) => selectTime(event.target.value)}
      />
    </div>
  );
}
